import { MetaTags } from '@/components/meta-tags'
import { Link, usePage } from '@inertiajs/react'
import { contact_path } from '@/lib/routes'

export default function Terms() {
  const {
    title,
    og_title,
    og_type,
    og_url,
    og_image,
    og_description,
    twitter_title,
    twitter_url,
    twitter_image,
    twitter_description,
  } = usePage().props
  return (
    <>
      <MetaTags
        title={title}
        description={og_description}
        og_title={og_title}
        og_type={og_type}
        og_url={og_url}
        og_image={og_image}
        og_description={og_description}
        twitter_title={twitter_title}
        twitter_url={twitter_url}
        twitter_image={twitter_image}
        twitter_description={twitter_description}
      />
      <div className="flex w-full flex-col items-center justify-start">
        <div className="container flex max-w-4xl flex-col items-center justify-start p-4 pt-[120px]">
          <article className="article-prose w-full">
            <h1>Terms of Service</h1>
            <p>
              <strong>Last Updated:</strong> December 04, 2025
            </p>

            <h3>Acceptance of Terms</h3>
            <p>
              By downloading, installing, or using picchat.ai (“App”), you agree
              to be bound by these Terms of Service. If you do not agree to
              these terms, please do not use the App.
            </p>

            <h3>Description of Service</h3>
            <p>
              The App provides tools for generating, editing, and viewing images
              on desktop platforms, with primary support for macOS and optional
              Windows/Linux support. The App allows you to connect your own API
              keys to third-party AI providers for image generation and related
              functionality.
            </p>

            <h3>Third-Party AI Providers and User-Provided API Keys</h3>
            <p>
              The App supports integration with external AI providers through a
              bring-your-own-key model. When using these integrations:
            </p>
            <ul>
              <li>
                Any prompts, instructions, images, or data you send through
                these features may be transmitted directly to the third-party
                provider you selected.
              </li>
              <li>
                We do not have access to, store, or transmit your API keys,
                prompts, or generated content.
              </li>
              <li>
                Use of third-party services is governed solely by the terms and
                privacy policies of those providers.
              </li>
              <li>
                You are responsible for reviewing and complying with the terms
                of any provider you connect to.
              </li>
            </ul>
            <p>
              We make no guarantees regarding the behavior, availability,
              safety, or output of any third-party AI service.
            </p>

            <h3 id="age-suitability">Age Suitability</h3>
            <p>
              Most AI providers require users to be at least 13 years old (or
              the minimum age required in their country) to create accounts and
              use their services. Some providers may require users to be 18
              years old or older in certain regions, such as the European
              Economic Area. Users under 18 may need parental or legal guardian
              permission depending on the provider and jurisdiction.
            </p>
            <p>
              picchat.ai does not enforce age requirements beyond those applied
              by the app store from which you download the App. However, you are
              responsible for ensuring that your use of any third-party AI
              provider complies with that provider's age requirements and terms
              of service. Different AI providers have varying age requirements
              and terms, and it is your responsibility to review and comply with
              each provider's policies before connecting your API keys and using
              their services.
            </p>
            <p>
              For example, OpenAI requires users to be at least 13 years old (or
              the minimum age in their country), and users under 18 must have
              parental or legal guardian permission. Google Gemini requires
              users to be 13 or older in most countries, and 18 or older in the
              European Economic Area, Switzerland, or the United Kingdom. These
              requirements may vary by provider and region, so please review
              each provider's terms before use.
            </p>

            <h3>User Responsibilities</h3>
            <p>As a user of the App, you are responsible for:</p>
            <ul>
              <li>Providing and safeguarding your own API keys</li>
              <li>
                Ensuring your use of third-party services complies with their
                terms and policies
              </li>
              <li>Backing up your content and generated images locally</li>
              <li>
                Using the App in compliance with applicable laws and platform
                rules
              </li>
              <li>
                Avoiding uploading or generating content that violates
                intellectual property, privacy, or safety regulations
              </li>
            </ul>

            <h3>Mac App Store and Platform-Specific Terms</h3>
            <p>
              When downloading the App through the Mac App Store, your use of
              the App is also governed by Apple's licensed application end-user
              agreement ("Apple EULA"). Where conflicts arise, the Apple EULA
              controls for App Store-distributed versions.
            </p>
            <p>
              The App does not automatically collect analytics or personal data
              beyond what is required by the operating system or the App Store
              distribution mechanism. However, both the App and our website may
              collect information you voluntarily provide (such as when signing
              up for newsletters, updates, or using optional features that
              connect to our webapp API), including your name, email address,
              platform preferences, and other details you choose to share.
            </p>

            <h3>Intellectual Property</h3>
            <p>
              The App and its original content, features, code, and design are
              owned by us and protected by intellectual property laws. These
              Terms do not transfer any rights to you beyond the limited license
              to use the App.
            </p>

            <h3>User Content</h3>
            <p>
              You retain all rights to any content or images created, uploaded,
              edited, or viewed using the App. We do not claim ownership of
              user-generated content, nor do we store or access it.
            </p>

            <h3>Prohibited Use</h3>
            <p>
              You agree not to use the App or third-party AI providers connected
              through it to:
            </p>
            <ul>
              <li>Create unlawful, harmful, or infringing content</li>
              <li>Violate export or trade restrictions</li>
              <li>
                Reverse-engineer, tamper with, or misappropriate third-party
                APIs
              </li>
              <li>Attempt unauthorized access to systems or resources</li>
            </ul>

            <h3>Disclaimer of Warranties</h3>
            <p>
              The App is provided “as is” without warranties of any kind,
              including merchantability, fitness for a particular purpose,
              accuracy of generative output, or reliability of third-party API
              behavior.
            </p>

            <h3>Limitation of Liability</h3>
            <p>
              To the fullest extent permitted by law, we are not liable for
              indirect, incidental, special, consequential, or punitive damages
              arising from your use of the App or third-party services,
              including loss of data or content.
            </p>

            <h3>Indemnification</h3>
            <p>
              You agree to indemnify and hold us harmless from claims, losses,
              or damages arising from your use of the App, your content, or your
              interactions with third-party AI providers.
            </p>

            <h3>Modifications to the App</h3>
            <p>
              We may update, change, suspend, or discontinue the App or its
              features at any time. We are not liable for any resulting loss of
              access, functionality, or content.
            </p>

            <h3>Updates to Terms</h3>
            <p>
              We may update these Terms periodically. The latest version will
              always be available within the App. Continued use of the App
              indicates acceptance of updated Terms.
            </p>

            <h3>Governing Law</h3>
            <p>
              These Terms are governed by the laws of California, USA, excluding
              conflict-of-law rules.
            </p>

            <h3>Contact Information</h3>
            <p>
              If you have questions about these Terms, use the contact form on
              the <Link href={contact_path()}>Contact</Link> page to reach out
              to us. We're here to help! Your feedback helps improve picchat.ai
              for you and everyone else.
            </p>
          </article>
        </div>
      </div>
    </>
  )
}
